import React, { useState, useEffect } from 'react';
import { ArrowLeft, ExternalLink, Clock, Calendar, Tag, Loader, AlertCircle, BookOpen, Newspaper, X, Filter, ChevronRight, Share2, RefreshCw, Pin } from 'lucide-react';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../Libs/firebase-config.mjs';


// Medium articles are saved into /medium-feed.json when the website is built (see scripts/fetch-medium.mjs),
// so the page does not depend on a third-party service. The old live service is only a fallback.
const MEDIUM_RSS = 'https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@bernardnjathi';
const fetchMediumFeed = async () => {
  try {
    const r = await fetch('/medium-feed.json', { cache: 'no-cache' });
    if (r.ok) {
      const data = await r.json();
      if (data && data.status === 'ok' && Array.isArray(data.items) && data.items.length) {
        return { ok: true, json: async () => data };
      }
    }
  } catch (e) { /* fall back below */ }
  return fetch(MEDIUM_RSS);
};

const CommunityBlogs = ({ isDark, onBack }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const categoryColors = {
    'Agriculture': isDark ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-green-50 text-green-700 border-green-200',
    'Data Analytics': isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-700 border-blue-200',
    'Finance': isDark ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-purple-50 text-purple-700 border-purple-200',
    'Sustainability': isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Technology': isDark ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-cyan-50 text-cyan-700 border-cyan-200',
    'Business': isDark ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-orange-50 text-orange-700 border-orange-200',
    'General': isDark ? 'bg-gray-500/10 text-gray-400 border-gray-500/20' : 'bg-gray-50 text-gray-700 border-gray-200'
  };

  const tagColors = {
    'Agriculture': isDark ? 'bg-green-500/15 text-green-300' : 'bg-green-100 text-green-700',
    'Technology': isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-100 text-blue-700',
    'Data': isDark ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-100 text-purple-700',
    'Finance': isDark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700',
    'Sustainability': isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
    'IoT': isDark ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-100 text-cyan-700',
    'Blockchain': isDark ? 'bg-orange-500/15 text-orange-300' : 'bg-orange-100 text-orange-700',
    'AI': isDark ? 'bg-red-500/15 text-red-300' : 'bg-red-100 text-red-700'
  };

  const extractImage = (content, description) => {
    const text = content || description || '';
    const imgMatch = text.match(/<img[^>]+src="([^">]+)"/);
    if (imgMatch && imgMatch[1]) return imgMatch[1];
    return null;
  };

  const categorizeContent = (title, content) => {
    const text = `${title} ${content}`.toLowerCase();
    if (text.match(/agri|farm|crop|soil|harvest|livestock/i)) return 'Agriculture';
    if (text.match(/data|analytic|ai|machine learning|algorithm/i)) return 'Data Analytics';
    if (text.match(/fintech|finance|payment|loan|credit|banking/i)) return 'Finance';
    if (text.match(/sustain|climate|environment|green|eco/i)) return 'Sustainability';
    if (text.match(/tech|digital|innovation|blockchain|iot/i)) return 'Technology';
    if (text.match(/business|market|trade|entrepreneur/i)) return 'Business';
    return 'General';
  };

  const extractTags = (title, content) => {
    const text = `${title} ${content}`.toLowerCase();
    const tags = [];
    const tagPatterns = {
      'Agriculture': /agri|farm|crop/i,
      'Technology': /tech|digital|innovation/i,
      'Data': /data|analytic|ai/i,
      'Finance': /fintech|finance|payment/i,
      'Sustainability': /sustain|climate|green/i,
      'IoT': /iot|sensor|device/i,
      'Blockchain': /blockchain|crypto/i,
      'AI': /\bai\b|artificial intelligence|machine learning/i
    };
    Object.entries(tagPatterns).forEach(([tag, pattern]) => {
      if (pattern.test(text)) tags.push(tag);
    });
    return tags.slice(0, 3);
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min`;
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    let formatted = content;
    
    formatted = formatted.replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold mb-6 mt-10">$1</h2>');
    formatted = formatted.replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold mb-4 mt-8">$1</h3>');
    formatted = formatted.replace(/^#### (.*$)/gim, '<h4 class="text-xl font-semibold mb-3 mt-6">$1</h4>');
    
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
    
    formatted = formatted.replace(/^\* (.*$)/gim, '<li class="mb-2">$1</li>');
    formatted = formatted.replace(/^- (.*$)/gim, '<li class="mb-2">$1</li>');
    
    formatted = formatted.replace(/(<li.*?<\/li>\s*)+/gs, '<ul class="list-disc ml-6 mb-6 space-y-2">$&</ul>');
    
    formatted = formatted.replace(/^\d+\. (.*$)/gim, '<li class="mb-2">$1</li>');
    formatted = formatted.replace(/(<li.*?<\/li>\s*)+/gs, (match) => {
      if (match.includes('class="list-disc')) return match;
      return '<ol class="list-decimal ml-6 mb-6 space-y-2">' + match + '</ol>';
    });
    
    const paragraphs = formatted.split(/\n\n+/);
    formatted = paragraphs.map(para => {
      para = para.trim();
      if (!para) return '';
      if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<ol') || para.startsWith('<li')) {
        return para;
      }
      return `<p class="mb-6 leading-relaxed">${para}</p>`;
    }).join('\n');
    
    return formatted;
  };

  // Create URL-friendly slug from title
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 60);
  };

  // Find blog by slug
  const findBlogBySlug = async (slug) => {
    try {
      // Try Medium blogs first
      const mediumResponse = await fetchMediumFeed();
      
      if (mediumResponse.ok) {
        const mediumData = await mediumResponse.json();
        if (mediumData.status === 'ok' && mediumData.items) {
          const mediumBlogs = mediumData.items.map(item => ({
            id: `medium-${item.guid}`,
            title: item.title,
            slug: createSlug(item.title),
            excerpt: item.description?.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
            content: item.content || item.description || '',
            category: categorizeContent(item.title, item.content || item.description || ''),
            source: 'Medium',
            sourceUrl: item.link,
            readTime: estimateReadTime(item.content || item.description || ''),
            date: new Date(item.pubDate).toISOString().split('T')[0],
            tags: extractTags(item.title, item.content || item.description || ''),
            author: item.author || 'Bernard Njathi',
            image: extractImage(item.content, item.description),
            canReadHere: false,
            contentBlocks: [],
            isPinned: false
          }));

          const foundBlog = mediumBlogs.find(blog => blog.slug === slug);
          if (foundBlog) {
            return foundBlog;
          }
        }
      }

      // Try Community blogs
      const blogsRef = query(collection(db, 'communitnblogs'), where('status', '==', 'published'));
      const snapshot = await getDocs(blogsRef);
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const blogSlug = createSlug(data.title || '');
        
        if (blogSlug === slug && data.status === 'published') {
          return {
            id: docSnap.id,
            title: data.title || 'Untitled',
            slug: blogSlug,
            excerpt: data.excerpt || data.content?.substring(0, 200) + '...' || '',
            content: data.content || '',
            category: data.category || 'General',
            source: 'Community',
            sourceUrl: null,
            readTime: estimateReadTime(data.content || ''),
            date: data.createdAt ? new Date(data.createdAt.toDate()).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            tags: data.tags || [],
            author: data.author || 'Imfuyo Team',
            image: data.coverImage || null,
            canReadHere: true,
            status: data.status || 'draft',
            createdAt: data.createdAt,
            contentBlocks: data.contentBlocks || [],
            kicker: data.kicker || '',
            isPinned: data.isPinned || false
          };
        }
      }

      return null;
    } catch (err) {
      console.error('Error finding blog by slug:', err);
      return null;
    }
  };

  const fetchBlogById = async (slugOrId) => {
    try {
      setLoading(true);
      setError(null);

      // Try to find by slug first
      const blog = await findBlogBySlug(slugOrId);
      
      if (blog) {
        setSelectedPost(blog);
        setShowReader(true);
        setLoading(false);
        return;
      }

      setError('Article not found or not available');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Unable to load the article. Please try again later.');
      setLoading(false);
    }
  };

  const handleShare = async (post, event = null) => {
    if (event) {
      event.stopPropagation();
    }

    const currentUrl = window.location.href.split('?')[0];
    const postSlug = post.slug || createSlug(post.title);
    const shareUrl = `${currentUrl}?article=${postSlug}`;
    setShareUrl(shareUrl);

    const metaTitle = document.querySelector('meta[property="og:title"]');
    const metaDescription = document.querySelector('meta[property="og:description"]');
    const metaImage = document.querySelector('meta[property="og:image"]');
    const metaUrl = document.querySelector('meta[property="og:url"]');

    if (metaTitle) metaTitle.setAttribute('content', post.title);
    if (metaDescription) metaDescription.setAttribute('content', post.excerpt);
    if (metaImage && post.image) metaImage.setAttribute('content', post.image);
    if (metaUrl) metaUrl.setAttribute('content', shareUrl);

    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await copyToClipboard(shareUrl);
        }
      }
    } else {
      await copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowShareMenu(true);
      setTimeout(() => setShowShareMenu(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareMenu(true);
      setTimeout(() => setShowShareMenu(false), 2000);
    }
  };

  const shareToSocial = (platform, post) => {
    const text = `Check out this article: ${post.title}`;
    const url = shareUrl || window.location.href;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      let allBlogs = [];

      if (selectedSource === 'all' || selectedSource === 'medium') {
        try {
          const mediumResponse = await fetchMediumFeed();
          if (mediumResponse.ok) {
            const mediumData = await mediumResponse.json();
            if (mediumData.status === 'ok' && mediumData.items) {
              const mediumBlogs = mediumData.items.map(item => ({
                id: `medium-${item.guid}`,
                title: item.title,
                slug: createSlug(item.title),
                excerpt: item.description?.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
                content: item.content || item.description || '',
                category: categorizeContent(item.title, item.content || item.description || ''),
                source: 'Medium',
                sourceUrl: item.link,
                readTime: estimateReadTime(item.content || item.description || ''),
                date: new Date(item.pubDate).toISOString().split('T')[0],
                tags: extractTags(item.title, item.content || item.description || ''),
                author: item.author || 'Bernard Njathi',
                image: extractImage(item.content, item.description),
                canReadHere: false,
                contentBlocks: [],
                isPinned: false
              }));
              allBlogs = [...allBlogs, ...mediumBlogs];
            }
          }
        } catch (err) {
          console.error('Error fetching Medium blogs:', err);
        }
      }

      if (selectedSource === 'all' || selectedSource === 'community') {
        try {
          const blogsRef = query(collection(db, 'communitnblogs'), where('status', '==', 'published'));
          const snapshot = await getDocs(blogsRef);
          
          const firebaseBlogs = snapshot.docs
            .map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.title || 'Untitled',
                slug: createSlug(data.title || 'untitled'),
                excerpt: data.excerpt || data.content?.substring(0, 200) + '...' || '',
                content: data.content || '',
                category: data.category || 'General',
                source: 'Community',
                sourceUrl: null,
                readTime: estimateReadTime(data.content || ''),
                date: data.createdAt ? new Date(data.createdAt.toDate()).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                tags: data.tags || [],
                author: data.author || 'Imfuyo Team',
                image: data.coverImage || null,
                canReadHere: true,
                status: data.status || 'draft',
                createdAt: data.createdAt,
                contentBlocks: data.contentBlocks || [],
                kicker: data.kicker || '',
                isPinned: data.isPinned || false
              };
            })
            .filter(blog => blog.status === 'published');
          
          allBlogs = [...allBlogs, ...firebaseBlogs];
        } catch (err) {
          console.error('Error fetching Firebase blogs:', err);
        }
      }

      // Sort: Pinned first, then by date
      allBlogs.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.date) - new Date(a.date);
      });

      setBlogs(allBlogs);
      if (allBlogs.length === 0) {
        setError('No articles available at the moment');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Unable to load articles at the moment. Please try again later.');
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const newUrl = window.location.pathname;
    window.history.pushState({}, '', newUrl);
    
    setShowReader(false);
    setSelectedPost(null);
    
    await fetchBlogs();
    setRefreshing(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const articleSlug = urlParams.get('article');
    const oldBlogId = urlParams.get('blog'); // Check for old URL format

    if (articleSlug) {
      fetchBlogById(articleSlug);
    } else if (oldBlogId) {
      // Handle old URL format by redirecting to new format
      fetchBlogByOldId(oldBlogId);
    } else {
      fetchBlogs();
    }
  }, [selectedSource]);

  // Handle old URL format with database IDs
  const fetchBlogByOldId = async (blogId) => {
    try {
      setLoading(true);
      setError(null);

      if (blogId.startsWith('medium-')) {
        const mediumResponse = await fetchMediumFeed();
        
        if (mediumResponse.ok) {
          const mediumData = await mediumResponse.json();
          if (mediumData.status === 'ok' && mediumData.items) {
            const foundItem = mediumData.items.find(item => `medium-${item.guid}` === blogId);
            if (foundItem) {
              const slug = createSlug(foundItem.title);
              // Redirect to new URL format
              const newUrl = `${window.location.pathname}?article=${slug}`;
              window.history.replaceState({}, '', newUrl);
              
              const blog = {
                id: blogId,
                title: foundItem.title,
                slug: slug,
                excerpt: foundItem.description?.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
                content: foundItem.content || foundItem.description || '',
                category: categorizeContent(foundItem.title, foundItem.content || foundItem.description || ''),
                source: 'Medium',
                sourceUrl: foundItem.link,
                readTime: estimateReadTime(foundItem.content || foundItem.description || ''),
                date: new Date(foundItem.pubDate).toISOString().split('T')[0],
                tags: extractTags(foundItem.title, foundItem.content || foundItem.description || ''),
                author: foundItem.author || 'Bernard Njathi',
                image: extractImage(foundItem.content, foundItem.description),
                canReadHere: false,
                contentBlocks: [],
                isPinned: false
              };
              
              setSelectedPost(blog);
              setShowReader(true);
              setLoading(false);
              return;
            }
          }
        }
      } else {
        const docRef = doc(db, 'communitnblogs', blogId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.status === 'published') {
            const slug = createSlug(data.title || 'untitled');
            // Redirect to new URL format
            const newUrl = `${window.location.pathname}?article=${slug}`;
            window.history.replaceState({}, '', newUrl);
            
            const blog = {
              id: docSnap.id,
              title: data.title || 'Untitled',
              slug: slug,
              excerpt: data.excerpt || data.content?.substring(0, 200) + '...' || '',
              content: data.content || '',
              category: data.category || 'General',
              source: 'Community',
              sourceUrl: null,
              readTime: estimateReadTime(data.content || ''),
              date: data.createdAt ? new Date(data.createdAt.toDate()).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              tags: data.tags || [],
              author: data.author || 'Imfuyo Team',
              image: data.coverImage || null,
              canReadHere: true,
              status: data.status || 'draft',
              createdAt: data.createdAt,
              contentBlocks: data.contentBlocks || [],
              isPinned: data.isPinned || false
            };

            setSelectedPost(blog);
            setShowReader(true);
            setLoading(false);
            return;
          }
        }
      }

      setError('Article not found or not available');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Unable to load the article. Please try again later.');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleReadPost = (post) => {
    if (post.canReadHere) {
      const postSlug = post.slug || createSlug(post.title);
      const newUrl = `${window.location.pathname}?article=${postSlug}`;
      window.history.pushState({ articleSlug: postSlug }, '', newUrl);
      
      setSelectedPost(post);
      setShowReader(true);
    } else {
      window.open(post.sourceUrl, '_blank');
    }
  };

  const closeReader = () => {
    const newUrl = window.location.pathname;
    window.history.pushState({}, '', newUrl);
    
    setShowReader(false);
    setSelectedPost(null);
  };

  // Blog Reader View
  if (showReader && selectedPost) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        <div className={`fixed top-20 left-0 right-0 z-40 border-b ${
          isDark ? 'bg-[#0a0a0a]/95 border-white/5' : 'bg-white/95 border-gray-200'
        }`} style={{ backdropFilter: 'blur(20px)' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={closeReader}
                className={`p-2 rounded-full transition-all hover:scale-105 ${
                  isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <div className="flex items-center space-x-3">
                {selectedPost.isPinned && (
                  <span className={`flex items-center space-x-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
                    isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                  }`}>
                    <Pin className="w-3 h-3 fill-current" />
                    <span>Pinned</span>
                  </span>
                )}
                <span className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
                  categoryColors[selectedPost.category] || categoryColors['General']
                }`}>
                  {selectedPost.category}
                </span>
                <div className="relative">
                  <button 
                    onClick={(e) => handleShare(selectedPost, e)}
                    className={`p-2 rounded-full transition-all hover:scale-105 ${
                      isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                    }`}
                  >
                    <Share2 className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                  </button>
                  
                  {showShareMenu && (
                    <div className={`absolute right-0 top-12 w-48 p-2 rounded-lg shadow-lg border ${
                      isDark ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'
                    }`}>
                      <div className={`text-xs font-medium mb-2 px-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        URL copied to clipboard!
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {selectedPost.image && (
          <div className="relative w-full h-[45vh] sm:h-[55vh] md:h-[65vh] mt-36">
            <img 
              src={selectedPost.image} 
              alt={selectedPost.title}
              className="w-full h-full object-cover"
              style={{ opacity: 1 }}
            />
          </div>
        )}

        <div className={`max-w-3xl mx-auto px-4 sm:px-6 ${selectedPost.image ? 'py-12 sm:py-16' : 'pt-44 pb-12 sm:pt-48 sm:pb-16'}`}>
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <Calendar className="w-4 h-4" />
              <span>{formatDate(selectedPost.date)}</span>
            </div>
            <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>•</span>
            <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <Clock className="w-4 h-4" />
              <span>{selectedPost.readTime} read</span>
            </div>
            <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>•</span>
            <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              By {selectedPost.author}
            </span>
          </div>

          {selectedPost.kicker && (
            <p className="text-xs font-bold tracking-[0.18em] uppercase text-green-700 mb-3">{selectedPost.kicker}</p>
          )}
          <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-[1.1] tracking-tight ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {selectedPost.title}
          </h1>
          {selectedPost.excerpt && selectedPost.source === 'Community' && (
            <p className={`text-xl leading-relaxed mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{selectedPost.excerpt}</p>
          )}

          {selectedPost.tags && selectedPost.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-12">
              {selectedPost.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                    tagColors[tag] || tagColors['General']
                  }`}
                >
                  <Tag className="w-3 h-3" />
                  <span>{tag}</span>
                </span>
              ))}
            </div>
          )}

          <article className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
            {selectedPost.contentBlocks && selectedPost.contentBlocks.length > 0 ? (
              selectedPost.contentBlocks.map((block, idx) => (
                <div key={block.id || idx} className="content-block">
                  {block.type === 'text' && block.blockType === 'h2' && (
                    <h2 className={`text-2xl sm:text-3xl font-bold font-serif mt-10 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'text' && block.blockType === 'h3' && (
                    <h3 className={`text-xl sm:text-2xl font-semibold font-serif mt-8 mb-3 ${isDark ? 'text-gray-100' : 'text-gray-800'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'text' && block.blockType === 'blockquote' && (
                    <blockquote className={`my-8 pl-5 border-l-4 border-green-600 italic text-xl font-serif leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'text' && block.blockType === 'code' && (
                    <pre className={`my-6 p-4 rounded-lg overflow-x-auto text-sm ${isDark ? 'bg-[#1e2e22] text-green-200' : 'bg-green-50 text-green-900'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'text' && !['h2', 'h3', 'blockquote', 'code'].includes(block.blockType) && (
                    <div
                      className={`text-lg leading-relaxed font-serif mb-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                      dangerouslySetInnerHTML={{ __html: formatContent(block.content) }}
                    />
                  )}

                  {block.type === 'ul' && (
                    <ul className={`list-disc ml-6 mb-6 space-y-2 text-lg font-serif leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'ol' && (
                    <ol className={`list-decimal ml-6 mb-6 space-y-2 text-lg font-serif leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`} dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'divider' && (
                    <div className="my-10 flex justify-center text-gray-400 tracking-[0.6em]">• • •</div>
                  )}
                  {block.type === 'code' && (
                    <pre className={`my-6 p-4 rounded-lg overflow-x-auto text-sm ${isDark ? 'bg-[#1e2e22] text-green-200' : 'bg-green-50 text-green-900'}`}>{block.content}</pre>
                  )}

                  {block.type === 'image' && block.imageUrl && (
                    <figure className="my-8 sm:my-12">
                      <div className="relative w-full overflow-hidden rounded-xl">
                        <img 
                          src={block.imageUrl} 
                          alt={block.caption || 'Article image'}
                          className="w-full h-auto object-cover"
                        />
                      </div>
                      {block.caption && (
                        <figcaption className={`text-sm text-center mt-4 italic ${
                          isDark ? 'text-gray-500' : 'text-gray-600'
                        }`}>
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  )}

                  {block.type === 'youtube' && block.youtubeUrl && (
                    <figure className="my-8 sm:my-12">
                      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${extractYouTubeId(block.youtubeUrl)}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={block.caption || 'YouTube video'}
                          className="absolute inset-0"
                        />
                      </div>
                      {block.caption && (
                        <figcaption className={`text-sm text-center mt-4 italic ${
                          isDark ? 'text-gray-500' : 'text-gray-600'
                        }`}>
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  )}
                </div>
              ))
            ) : (
              <div 
                className={`text-lg leading-relaxed font-serif ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                dangerouslySetInnerHTML={{ __html: formatContent(selectedPost.content) }}
              />
            )}
          </article>

          <div className={`mt-12 sm:mt-16 pt-8 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                Published on {formatDate(selectedPost.date)}
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={(e) => handleShare(selectedPost, e)}
                  className={`text-sm font-medium flex items-center space-x-2 ${
                    isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'
                  }`}
                >
                  <Share2 className="w-4 h-4" />
                  <span>Share Article</span>
                </button>
                <button
                  onClick={closeReader}
                  className={`text-sm font-medium flex items-center space-x-2 ${
                    isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'
                  }`}
                >
                  <span>Back to articles</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .prose h2 {
            color: ${isDark ? '#fff' : '#111827'};
            font-weight: 700;
            line-height: 1.3;
            font-family: serif;
          }
          .prose h3 {
            color: ${isDark ? '#e5e7eb' : '#1f2937'};
            font-weight: 600;
            line-height: 1.4;
            font-family: serif;
          }
          .prose h4 {
            color: ${isDark ? '#d1d5db' : '#374151'};
            font-weight: 600;
            font-family: serif;
          }
          .prose p {
            color: ${isDark ? '#d1d5db' : '#374151'};
            line-height: 1.8;
            font-size: 1.125rem;
            font-family: serif;
          }
          .prose strong {
            color: ${isDark ? '#fff' : '#111827'};
            font-weight: 600;
          }
          .prose em {
            color: ${isDark ? '#e5e7eb' : '#4b5563'};
          }
          .prose ul, .prose ol {
            color: ${isDark ? '#d1d5db' : '#374151'};
          }
          .prose li {
            line-height: 1.7;
          }
          .prose ul {
            list-style-type: disc;
          }
          .prose ol {
            list-style-type: decimal;
          }
        `}</style>
      </div>
    );
  }

  // Main Blog List View
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <div className={`relative z-40 border-b ${
        isDark ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-200'
      }`} style={{ paddingTop: '80px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-4">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={onBack}
                className={`p-2 rounded-full transition-all hover:scale-105 ${
                  isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Newspaper className={`w-6 h-6 sm:w-8 sm:h-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-serif font-bold tracking-tight ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  The Imfuyo Times
                </h1>
              </div>
            </div>

            <div className="sm:hidden flex items-center justify-between gap-2">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium ${
                  isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Filter</span>
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  refreshing 
                    ? isDark ? 'bg-white/5 text-gray-600' : 'bg-gray-50 text-gray-400'
                    : isDark ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Loading...' : 'Refresh'}</span>
              </button>
            </div>

            <div className="hidden sm:flex items-center space-x-2">
              <Filter className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-600'}`} />
              <button
                onClick={() => setSelectedSource('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedSource === 'all'
                    ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-600 text-white'
                    : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedSource('medium')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedSource === 'medium'
                    ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-600 text-white'
                    : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Medium
              </button>
              <button
                onClick={() => setSelectedSource('community')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedSource === 'community'
                    ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-600 text-white'
                    : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Community
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className={`ml-2 p-2 rounded-full transition-all ${
                  refreshing 
                    ? isDark ? 'bg-white/5 text-gray-600' : 'bg-gray-50 text-gray-400'
                    : isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
                title="Refresh articles"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {showMobileFilters && (
            <div className="sm:hidden mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setSelectedSource('all');
                    setShowMobileFilters(false);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedSource === 'all'
                      ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-600 text-white'
                      : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => {
                    setSelectedSource('medium');
                    setShowMobileFilters(false);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedSource === 'medium'
                      ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-600 text-white'
                      : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Medium
                </button>
                <button
                  onClick={() => {
                    setSelectedSource('community');
                    setShowMobileFilters(false);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedSource === 'community'
                      ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-600 text-white'
                      : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Community
                </button>
              </div>
            </div>
          )}

          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            Insights on livestock, data and finance • Stories from Imfuyo and the farmers we work with
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <Loader className={`w-12 h-12 animate-spin mb-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Loading latest articles...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32">
            <AlertCircle className={`w-12 h-12 mb-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <p className={`text-lg mb-6 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all hover:scale-105 ${
                refreshing 
                  ? isDark ? 'bg-white/5 text-gray-600' : 'bg-gray-100 text-gray-400'
                  : isDark ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh Articles'}</span>
            </button>
          </div>
        ) : blogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <BookOpen className={`w-12 h-12 mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-lg mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              No articles available for this source
            </p>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all hover:scale-105 ${
                refreshing 
                  ? isDark ? 'bg-white/5 text-gray-600' : 'bg-gray-100 text-gray-400'
                  : isDark ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh Articles'}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-12 sm:space-y-20">
            {blogs[0] && (
              <article className="group cursor-pointer" onClick={() => handleReadPost(blogs[0])}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
                  {blogs[0].image && (
                    <div className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden rounded-2xl">
                      <img 
                        src={blogs[0].image} 
                        alt={blogs[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      {blogs[0].isPinned && (
                        <div className="absolute top-4 left-4">
                          <span className={`flex items-center space-x-1.5 px-3 py-2 rounded-full font-bold text-sm shadow-lg ${
                            isDark ? 'bg-amber-500 text-white' : 'bg-amber-400 text-gray-900'
                          }`}>
                            <Pin className="w-4 h-4 fill-current" />
                            <span>PINNED</span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={!blogs[0].image ? "lg:col-span-2" : ""}>
                    <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                      {!blogs[0].image && blogs[0].isPinned && (
                        <span className={`flex items-center space-x-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
                          isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                        }`}>
                          <Pin className="w-3 h-3 fill-current" />
                          <span>PINNED</span>
                        </span>
                      )}
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                        categoryColors[blogs[0].category] || categoryColors['General']
                      }`}>
                        {blogs[0].category}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        blogs[0].source === 'Medium' 
                          ? isDark ? 'bg-black text-white' : 'bg-gray-900 text-white'
                          : isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                      }`}>
                        {blogs[0].source}
                      </span>
                    </div>

                    <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 sm:mb-6 leading-tight group-hover:opacity-80 transition-opacity ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {blogs[0].title}
                    </h2>

                    <p className={`text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {blogs[0].excerpt}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(blogs[0].date)}</span>
                      </div>
                      <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>•</span>
                      <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        <Clock className="w-4 h-4" />
                        <span>{blogs[0].readTime}</span>
                      </div>
                      <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>•</span>
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        By {blogs[0].author}
                      </span>
                    </div>

                    {blogs[0].tags && blogs[0].tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
                        {blogs[0].tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                              tagColors[tag] || tagColors['General']
                            }`}
                          >
                            <Tag className="w-3 h-3" />
                            <span>{tag}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadPost(blogs[0]);
                        }}
                        className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all hover:scale-105 ${
                          isDark 
                            ? 'bg-white text-black hover:bg-gray-100' 
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        <span>{blogs[0].canReadHere ? 'Read Article' : 'Read on Medium'}</span>
                        {blogs[0].canReadHere ? <ChevronRight className="w-5 h-5" /> : <ExternalLink className="w-5 h-5" />}
                      </button>
                      
                      <button
                        onClick={(e) => handleShare(blogs[0], e)}
                        className={`inline-flex items-center space-x-2 px-4 py-3 rounded-full font-medium transition-all ${
                          isDark 
                            ? 'bg-white/5 text-white hover:bg-white/10' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Share2 className="w-4 h-4" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            )}

            {blogs.slice(1).length > 0 && (
              <section>
                <h3 className={`text-xl sm:text-2xl font-serif font-bold mb-8 sm:mb-10 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Latest Stories
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {blogs.slice(1).map((post) => (
                    <article
                      key={post.id}
                      className={`group cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                        post.isPinned
                          ? isDark 
                            ? 'bg-gradient-to-br from-amber-900/20 to-gray-900 border-2 border-amber-500/40' 
                            : 'bg-gradient-to-br from-amber-50 to-white border-2 border-amber-300'
                          : isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => handleReadPost(post)}
                    >
                      {post.image && (
                        <div className="relative h-48 sm:h-56 overflow-hidden">
                          <img 
                            src={post.image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          {post.isPinned && (
                            <div className="absolute top-3 left-3">
                              <span className={`flex items-center space-x-1 px-2.5 py-1 rounded-full font-bold text-xs shadow-lg ${
                                isDark ? 'bg-amber-500 text-white' : 'bg-amber-400 text-gray-900'
                              }`}>
                                <Pin className="w-3 h-3 fill-current" />
                                <span>PINNED</span>
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="p-4 sm:p-6">
                        <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                          {!post.image && post.isPinned && (
                            <span className={`flex items-center space-x-1 text-xs font-bold px-2.5 py-1 rounded-full ${
                              isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                            }`}>
                              <Pin className="w-2.5 h-2.5 fill-current" />
                              <span>PINNED</span>
                            </span>
                          )}
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                            categoryColors[post.category] || categoryColors['General']
                          }`}>
                            {post.category}
                          </span>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            post.source === 'Medium' 
                              ? isDark ? 'bg-black text-white' : 'bg-gray-900 text-white'
                              : isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                          }`}>
                            {post.source}
                          </span>
                        </div>

                        <h4 className={`text-lg sm:text-xl font-serif font-bold mb-2 sm:mb-3 leading-tight line-clamp-2 group-hover:opacity-80 transition-opacity ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {post.title}
                        </h4>

                        <p className={`text-sm mb-3 sm:mb-4 leading-relaxed line-clamp-3 ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {post.excerpt}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4 text-xs">
                          <div className={`flex items-center space-x-1.5 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{formatDate(post.date)}</span>
                          </div>
                          <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>•</span>
                          <div className={`flex items-center space-x-1.5 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                            <Clock className="w-3.5 h-3.5" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3 sm:mb-4">
                            {post.tags.slice(0, 2).map((tag, idx) => (
                              <span
                                key={idx}
                                className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  tagColors[tag] || tagColors['General']
                                }`}
                              >
                                <Tag className="w-2.5 h-2.5" />
                                <span>{tag}</span>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadPost(post);
                            }}
                            className={`text-sm font-medium flex items-center space-x-1.5 transition-all group-hover:translate-x-1 ${
                              isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'
                            }`}
                          >
                            <span>{post.canReadHere ? 'Read More' : 'Read on Medium'}</span>
                            {post.canReadHere ? <ChevronRight className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                          </button>

                          <button
                            onClick={(e) => handleShare(post, e)}
                            className={`p-2 rounded-full transition-all hover:scale-105 ${
                              isDark ? 'hover:bg-white/5' : 'hover:bg-gray-200'
                            }`}
                          >
                            <Share2 className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {showShareMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowShareMenu(false)}>
          <div className={`max-w-md w-full mx-4 p-6 rounded-2xl ${
            isDark ? 'bg-[#1a1a1a]' : 'bg-white'
          }`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Share Article
              </h3>
              <button
                onClick={() => setShowShareMenu(false)}
                className={`p-1 rounded-full ${
                  isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                onClick={() => shareToSocial('whatsapp', selectedPost)}
                className={`flex-1 min-w-[100px] p-3 rounded-lg flex flex-col items-center space-y-2 ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <span className="text-lg">📱</span>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp</span>
              </button>
              
              <button
                onClick={() => shareToSocial('facebook', selectedPost)}
                className={`flex-1 min-w-[100px] p-3 rounded-lg flex flex-col items-center space-y-2 ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <span className="text-lg">📘</span>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Facebook</span>
              </button>
              
              <button
                onClick={() => shareToSocial('twitter', selectedPost)}
                className={`flex-1 min-w-[100px] p-3 rounded-lg flex flex-col items-center space-y-2 ${
                  isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <span className="text-lg">🐦</span>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Twitter</span>
              </button>
            </div>
            
            <div className={`p-3 rounded-lg ${
              isDark ? 'bg-white/5' : 'bg-gray-100'
            }`}>
              <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Article URL
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className={`flex-1 text-sm bg-transparent border-none outline-none ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                />
                <button
                  onClick={() => copyToClipboard(shareUrl)}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                  }`}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .grid {
            grid-template-columns: 1fr;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        html {
          scroll-behavior: smooth;
        }

        .aspect-video {
          aspect-ratio: 16 / 9;
        }

        .font-serif {
          font-family: 'Times New Roman', Times, serif;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default CommunityBlogs;